import React, { useMemo, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import GetAppIcon from '@material-ui/icons/GetApp';
import { formatDayLabel, formatMetric, formatMoney, formatNumber, localDate } from './format';
import { downloadGoogleCampaignSpendTableXlsx } from './exportCampaignSpendTable';

const METRICS_PER_DAY = 7;
const STICKY_LEAD_WIDTH = 52;
const STICKY_CAMPAIGN_WIDTH = 280;
const STICKY_TOTAL_WIDTH = STICKY_LEAD_WIDTH + STICKY_CAMPAIGN_WIDTH;
const HEADER_ROW1_HEIGHT_PX = 48;
const PERIOD_COLUMN_COUNT = 15;

function sumSalesWonForDates(dates, getDayMetrics) {
    return (dates || []).reduce((acc, date) => {
        const day = getDayMetrics ? getDayMetrics(date) : {};
        acc.salesWon += day.salesWon || 0;
        acc.salesWonAmount += day.salesWonAmount || 0;
        return acc;
    }, { salesWon: 0, salesWonAmount: 0 });
}

function withPeriodSalesWon(metrics, dates, getDayMetrics) {
    if (!dates?.length || !getDayMetrics) return metrics;
    const won = sumSalesWonForDates(dates, getDayMetrics);
    return { ...metrics, salesWon: won.salesWon, salesWonAmount: won.salesWonAmount };
}

const useStyles = makeStyles(() => ({
    card: {
        backgroundColor: '#fff',
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
    },
    heading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        padding: '18px 20px 0',
    },
    title: {
        margin: 0,
        fontSize: 18,
        fontWeight: 700,
        color: '#1C375D',
    },
    count: {
        color: '#8a92a3',
        fontSize: 14,
    },
    exportBtn: {
        textTransform: 'none',
        fontWeight: 600,
        backgroundColor: '#1C375D',
        color: '#fff',
        '&:hover': { backgroundColor: '#152a47' },
    },
    scrollHint: {
        margin: '0 20px 8px',
        fontSize: 12,
        color: '#6b7a90',
    },
    tableScroll: {
        maxHeight: 'calc(100vh - 340px)',
        overflowX: 'auto',
        overflowY: 'auto',
        overscrollBehaviorX: 'contain',
        WebkitOverflowScrolling: 'touch',
        padding: '0 0 16px',
        position: 'relative',
        isolation: 'isolate',
    },
    table: {
        width: 'max-content',
        minWidth: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        tableLayout: 'auto',
        textAlign: 'left',
    },
    headCell: {
        padding: '14px 12px',
        fontWeight: 700,
        backgroundColor: '#1C375D',
        color: '#FFF2EB',
        whiteSpace: 'nowrap',
        fontSize: 12,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        boxShadow: '0 2px 4px rgba(16, 24, 40, 0.12)',
    },
    headCellStickyRow1: {
        position: 'sticky',
        top: 0,
        zIndex: 12,
    },
    headCellStickyRow2: {
        position: 'sticky',
        top: HEADER_ROW1_HEIGHT_PX,
        zIndex: 11,
    },
    stickyLead: {
        position: 'sticky',
        left: 0,
        zIndex: 24,
        backgroundColor: '#1C375D',
        boxShadow: '2px 0 4px rgba(16, 24, 40, 0.08)',
    },
    stickyLeadBody: {
        position: 'sticky',
        left: 0,
        zIndex: 5,
        backgroundColor: '#fff',
        boxShadow: '2px 0 4px rgba(16, 24, 40, 0.06)',
    },
    stickyNameHead: {
        position: 'sticky',
        left: STICKY_LEAD_WIDTH,
        zIndex: 23,
        minWidth: STICKY_CAMPAIGN_WIDTH,
        width: STICKY_CAMPAIGN_WIDTH,
        maxWidth: STICKY_CAMPAIGN_WIDTH,
        backgroundColor: '#1C375D',
        boxShadow: '4px 0 8px rgba(16, 24, 40, 0.12)',
    },
    stickyNameBody: {
        position: 'sticky',
        left: STICKY_LEAD_WIDTH,
        zIndex: 5,
        minWidth: STICKY_CAMPAIGN_WIDTH,
        width: STICKY_CAMPAIGN_WIDTH,
        maxWidth: STICKY_CAMPAIGN_WIDTH,
        backgroundColor: '#fff',
        boxShadow: '4px 0 8px rgba(16, 24, 40, 0.08)',
        verticalAlign: 'top',
    },
    jumpTodayBtn: {
        marginLeft: 8,
        padding: 0,
        border: 'none',
        background: 'none',
        color: '#1C375D',
        fontWeight: 600,
        fontSize: 12,
        cursor: 'pointer',
        textDecoration: 'underline',
    },
    datePageHead: {
        textAlign: 'center',
        borderLeft: '3px solid rgba(255, 242, 235, 0.35)',
        textTransform: 'none',
        letterSpacing: 0,
        fontSize: 13,
    },
    datePageHeadToday: {
        backgroundColor: '#163049',
        borderLeftColor: '#F47E46',
    },
    dateSubHead: {
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        borderLeft: '1px solid rgba(255, 242, 235, 0.2)',
    },
    datePageCell: {
        borderLeft: '1px solid #eef0f3',
        backgroundColor: '#fcfdfe',
        minWidth: 72,
    },
    datePageStart: {
        borderLeft: '3px solid #d5dbe3',
    },
    datePageStartToday: {
        borderLeftColor: '#F47E46',
        backgroundColor: '#fffaf6',
    },
    datePageHeadSectionStart: {
        borderLeft: '3px solid rgba(255, 242, 235, 0.35)',
    },
    numHead: { textAlign: 'right' },
    headStack: {
        whiteSpace: 'normal',
        lineHeight: 1.25,
        textTransform: 'none',
        fontSize: 11,
        letterSpacing: 0.2,
        verticalAlign: 'bottom',
    },
    bodyCell: {
        padding: '12px',
        fontSize: 13.5,
        borderBottom: '1px solid #eef0f3',
        verticalAlign: 'middle',
    },
    numCell: {
        textAlign: 'right',
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
    },
    name: {
        display: 'block',
        fontWeight: 700,
        color: '#1C375D',
        lineHeight: 1.35,
    },
    id: {
        display: 'block',
        color: '#8a92a3',
        marginTop: 4,
        fontSize: 12,
    },
    sub: {
        display: 'inline-block',
        marginTop: 4,
        padding: '1px 6px',
        borderRadius: 4,
        backgroundColor: '#e7eef8',
        color: '#1C375D',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
    },
    cityTotalRow: {
        backgroundColor: '#eef3f8',
        cursor: 'pointer',
        '& td': {
            fontWeight: 700,
            color: '#1C375D',
            borderBottom: '2px solid #d5dbe3',
        },
        '& $stickyLeadBody, & $stickyNameBody': { backgroundColor: '#eef3f8' },
        '&:hover': { backgroundColor: '#e4ebf3' },
        '&:hover $stickyLeadBody, &:hover $stickyNameBody': { backgroundColor: '#e4ebf3' },
    },
    rowAlt: {
        backgroundColor: '#fafbfc',
        '& $stickyLeadBody, & $stickyNameBody': { backgroundColor: '#fafbfc' },
    },
    totalRow: {
        backgroundColor: '#fff8f3',
        '& td': { fontWeight: 700, borderTop: '2px solid #F47E46' },
        '& $stickyLeadBody, & $stickyNameBody': { backgroundColor: '#fff8f3' },
    },
    leadLink: {
        border: 'none',
        padding: 0,
        margin: 0,
        background: 'none',
        color: '#1C375D',
        fontWeight: 700,
        fontSize: 'inherit',
        fontVariantNumeric: 'tabular-nums',
        cursor: 'pointer',
        textDecoration: 'underline',
        '&:hover': { color: '#0f2844' },
    },
    cityField: {
        minWidth: 140,
        marginTop: 6,
    },
    empty: {
        padding: '28px 20px',
        color: '#8a92a3',
        fontSize: 14,
    },
    targetInput: {
        width: 76,
        boxSizing: 'border-box',
        textAlign: 'right',
        border: '1px solid #d5dbe3',
        borderRadius: 6,
        padding: '6px 8px',
        fontSize: 13,
        color: '#1C375D',
        fontVariantNumeric: 'tabular-nums',
        backgroundColor: '#fff',
        '&:focus': {
            outline: '2px solid #1C375D33',
            borderColor: '#1C375D',
        },
    },
    met: {
        color: '#176b35',
        fontWeight: 700,
    },
    mid: {
        color: '#8a6a12',
        fontWeight: 700,
    },
    low: {
        color: '#a61b1b',
        fontWeight: 700,
    },
}));

function achievedPercent(totalReal, target) {
    if (target == null || target <= 0 || totalReal == null) return null;
    return Math.round((totalReal / target) * 100);
}

function percentClass(classes, percent) {
    if (percent == null) return '';
    if (percent >= 100) return classes.met;
    if (percent >= 60) return classes.mid;
    return classes.low;
}

function TargetCell({ city, savedTarget, classes, onSave }) {
    const saved = savedTarget == null ? '' : String(savedTarget);
    const [value, setValue] = React.useState(saved);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        setValue(saved);
    }, [saved]);

    async function commit() {
        if (saving || value === saved) return;
        const next = value === '' ? null : Number(value);
        setSaving(true);
        try {
            await onSave(city, next);
        } catch (error) {
            setValue(saved);
        } finally {
            setSaving(false);
        }
    }

    return (
        <input
            className={classes.targetInput}
            inputMode="numeric"
            aria-label={`Lead target for ${city}`}
            value={value}
            disabled={saving}
            placeholder="Set"
            onChange={(event) => setValue(event.target.value.replace(/[^\d]/g, '').slice(0, 7))}
            onBlur={commit}
            onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
            }}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
        />
    );
}

function isTargetInteraction(event) {
    return Boolean(event.target.closest('[data-lead-target-cell]'));
}

function eachDate(from, to) {
    if (!from || !to || from > to) return [];
    const dates = [];
    const cursor = new Date(`${from}T00:00:00Z`);
    const end = new Date(`${to}T00:00:00Z`);
    while (cursor <= end) {
        dates.push(cursor.toISOString().slice(0, 10));
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return dates.reverse();
}

function primaryCity(campaign) {
    return campaign.cities?.[0] || 'Untagged';
}

function emptyDay() {
    return {
        spend: 0,
        leadCount: 0,
        sqlReal: 0,
        salesTbc: 0,
        totalReal: 0,
        salesWon: 0,
        salesWonAmount: 0,
    };
}

function isMetaCampaign(campaign) {
    return campaign?.channel === 'meta' || String(campaign?.campaignId || '').startsWith('meta:');
}

function recordCampaignId(campaign) {
    if (campaign?.reportingCampaignId) return String(campaign.reportingCampaignId);
    const id = String(campaign?.campaignId || '');
    if (id.startsWith('meta:')) return id.slice(5);
    if (id.startsWith('google:')) return id.slice(7);
    return id;
}

function normalizeDayMetrics(day) {
    const base = day || emptyDay();
    const sqlReal = Number(base.sqlReal) || 0;
    const salesTbc = Number(base.salesTbc) || 0;
    const totalReal = base.totalReal != null
        ? Number(base.totalReal) || 0
        : sqlReal + salesTbc;
    return {
        spend: Number(base.spend) || 0,
        leadCount: Number(base.leadCount) || 0,
        sqlReal,
        salesTbc,
        totalReal,
        salesWon: Number(base.salesWon) || 0,
        salesWonAmount: Number(base.salesWonAmount) || 0,
    };
}

function metaCampaignDay(metaCityDaily, campaign, date) {
    const store = metaCityDaily?.campaigns;
    if (!store) return null;
    const id = recordCampaignId(campaign);
    const day = store[id]?.[date];
    return day ? normalizeDayMetrics(day) : null;
}

function LeadCountCell({ classes, cellClass, count, onOpen }) {
    const cell = cellClass || `${classes.bodyCell} ${classes.numCell}`;
    const value = count || 0;
    if (!value || !onOpen) {
        return <td className={cell}>{formatNumber(value)}</td>;
    }
    return (
        <td className={cell}>
            <button type="button" className={classes.leadLink} onClick={(event) => { event.stopPropagation(); onOpen(); }}>
                {formatNumber(value)}
            </button>
        </td>
    );
}

function GoogleDayPageCells({ classes, day, isToday, isFirstDateColumn, rows, label, onOpenLeads, date, mixedCurrency, currency }) {
    const sectionStart = isFirstDateColumn ? ` ${classes.datePageHeadSectionStart}` : '';
    const start = isToday
        ? `${classes.datePageCell} ${classes.datePageStart} ${classes.datePageStartToday}${sectionStart}`
        : `${classes.datePageCell} ${classes.datePageStart}${sectionStart}`;
    const cell = `${classes.bodyCell} ${classes.numCell}`;
    const money = (value) => (mixedCurrency ? '—' : formatMoney(value, currency || 'INR'));
    const totalReal = day.totalReal != null ? day.totalReal : (day.leadCount || 0);
    const dayCperp = totalReal > 0 && day.spend ? day.spend / totalReal : null;
    const open = (bucket) => {
        if (!onOpenLeads) return undefined;
        const ids = rows.map((row) => row.campaignId).filter(Boolean);
        return () => onOpenLeads(bucket, ids, label, date);
    };
    return (
        <>
            <td className={`${start} ${cell}`}>{money(day.spend)}</td>
            <LeadCountCell classes={classes} cellClass={`${classes.datePageCell} ${cell}`} count={day.leadCount} onOpen={open('received')} />
            <LeadCountCell classes={classes} cellClass={`${classes.datePageCell} ${cell}`} count={day.sqlReal} onOpen={open('sql')} />
            <LeadCountCell classes={classes} cellClass={`${classes.datePageCell} ${cell}`} count={day.salesTbc} onOpen={open('tbc')} />
            <LeadCountCell classes={classes} cellClass={`${classes.datePageCell} ${cell}`} count={day.totalReal} onOpen={open('real')} />
            <LeadCountCell classes={classes} cellClass={`${classes.datePageCell} ${cell}`} count={day.salesWon} onOpen={open('won')} />
            <td className={`${classes.datePageCell} ${cell}`}>
                {formatMetric(dayCperp, money)}
            </td>
        </>
    );
}

function GooglePeriodCells({
    classes,
    metrics,
    rows,
    label,
    dates,
    today,
    getDayMetrics,
    onOpenLeads,
    mixedCurrency,
    currency,
    targetCity,
    savedTarget,
    onSaveTarget,
    displayTarget,
    displayAchievedPercent,
}) {
    const rowCurrency = metrics.currency || currency || 'INR';
    const cell = `${classes.bodyCell} ${classes.numCell}`;
    const money = (value) => (mixedCurrency ? '—' : formatMoney(value, rowCurrency));
    const cpc = metrics.clicks > 0 ? metrics.spend / metrics.clicks : null;
    const cperp = metrics.totalReal > 0 ? metrics.spend / metrics.totalReal : null;
    const percent = displayAchievedPercent != null
        ? displayAchievedPercent
        : achievedPercent(metrics.totalReal, targetCity ? savedTarget : null);
    const targetLabel = displayTarget != null
        ? formatNumber(displayTarget)
        : (savedTarget != null ? formatNumber(savedTarget) : '—');
    const open = (bucket) => {
        if (!onOpenLeads) return undefined;
        const ids = rows.map((row) => row.campaignId).filter(Boolean);
        return () => onOpenLeads(bucket, ids, label);
    };
    return (
        <>
            <td className={cell}>{money(metrics.spend)}</td>
            <td className={cell}>{formatNumber(metrics.impressions)}</td>
            <td className={cell}>{formatNumber(metrics.clicks)}</td>
            <td className={cell}>{formatMetric(cpc, (value) => formatMoney(value, rowCurrency))}</td>
            <td
                className={cell}
                data-lead-target-cell={targetCity && onSaveTarget ? '' : undefined}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
            >
                {targetCity && onSaveTarget ? (
                    <TargetCell
                        city={targetCity}
                        savedTarget={savedTarget}
                        classes={classes}
                        onSave={onSaveTarget}
                    />
                ) : (
                    targetLabel
                )}
            </td>
            <LeadCountCell classes={classes} cellClass={cell} count={metrics.leadCount} onOpen={open('received')} />
            <LeadCountCell classes={classes} cellClass={cell} count={metrics.sqlReal} onOpen={open('sql')} />
            <LeadCountCell classes={classes} cellClass={cell} count={metrics.salesTbc} onOpen={open('tbc')} />
            <LeadCountCell classes={classes} cellClass={cell} count={metrics.totalReal} onOpen={open('real')} />
            <LeadCountCell classes={classes} cellClass={cell} count={metrics.salesWon} onOpen={open('won')} />
            <td className={cell}>{formatMoney(metrics.salesWonAmount || 0, rowCurrency)}</td>
            <td className={`${cell} ${percentClass(classes, percent)}`}>
                {percent == null ? '—' : `${percent}%`}
            </td>
            <td className={cell}>{formatMetric(cperp, (value) => formatMoney(value, rowCurrency))}</td>
            {dates.map((date, dateIndex) => (
                <GoogleDayPageCells
                    key={date}
                    classes={classes}
                    day={getDayMetrics(date)}
                    isToday={date === today}
                    isFirstDateColumn={dateIndex === 0}
                    rows={rows}
                    label={label}
                    onOpenLeads={onOpenLeads}
                    date={date}
                    mixedCurrency={mixedCurrency}
                    currency={rowCurrency}
                />
            ))}
        </>
    );
}

export default function GoogleCampaignSpendTable({
    campaigns,
    records,
    from,
    to,
    loading,
    hasError,
    scopeLabel,
    emptyMessage,
    onOpenLeads,
    cityOptions,
    onTagCity,
    taggingId,
    currency,
    mixedCurrency,
    targets,
    onSaveTarget,
    resolvePrimaryCity,
    tableTitle = 'Campaign breakdown',
    hideCityTagging = false,
    metaCityDaily = null,
}) {
    const cityKey = resolvePrimaryCity || primaryCity;
    const classes = useStyles();
    const cityTargets = targets || {};
    const scrollRef = useRef(null);
    const [openCities, setOpenCities] = useState({ Untagged: true });
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
    const today = localDate();
    const dates = useMemo(() => eachDate(from, to), [from, to]);

    const spendByKey = useMemo(() => {
        const map = new Map();
        (records || []).forEach((row) => {
            const date = String(row.date || '').slice(0, 10);
            const key = `${row.campaignId}|${date}`;
            map.set(key, (map.get(key) || 0) + (Number(row.spend) || 0));
        });
        return map;
    }, [records]);

    const groups = useMemo(() => {
        const grouped = new Map();
        campaigns.forEach((campaign) => {
            const cityName = cityKey(campaign);
            const rows = grouped.get(cityName) || [];
            rows.push(campaign);
            grouped.set(cityName, rows);
        });
        return [...grouped.entries()].sort(([left], [right]) => {
            if (left === 'Untagged') return 1;
            if (right === 'Untagged') return -1;
            return left.localeCompare(right);
        });
    }, [campaigns, cityKey]);

    function campaignDay(campaign, date) {
        if (isMetaCampaign(campaign)) {
            const metaDay = metaCampaignDay(metaCityDaily, campaign, date);
            if (metaDay) return metaDay;
        }
        const lead = (campaign.days || []).find((day) => day.date === date) || emptyDay();
        const spend = spendByKey.get(`${recordCampaignId(campaign)}|${date}`) || 0;
        return {
            spend,
            leadCount: Number(lead.leadCount) || 0,
            sqlReal: Number(lead.sqlReal) || 0,
            salesTbc: Number(lead.salesTbc) || 0,
            totalReal: Number(lead.totalReal) || ((Number(lead.sqlReal) || 0) + (Number(lead.salesTbc) || 0)),
            salesWon: Number(lead.salesWon) || 0,
            salesWonAmount: Number(lead.salesWonAmount) || 0,
        };
    }

    function sumDays(rows, date) {
        return rows.reduce((sum, row) => {
            const day = campaignDay(row, date);
            return {
                spend: sum.spend + day.spend,
                leadCount: sum.leadCount + day.leadCount,
                sqlReal: sum.sqlReal + day.sqlReal,
                salesTbc: sum.salesTbc + day.salesTbc,
                totalReal: sum.totalReal + day.totalReal,
                salesWon: sum.salesWon + day.salesWon,
                salesWonAmount: sum.salesWonAmount + day.salesWonAmount,
            };
        }, emptyDay());
    }

    function rangeOf(rows) {
        return rows.reduce((sum, campaign) => ({
            spend: sum.spend + (campaign.spend || 0),
            impressions: sum.impressions + (campaign.impressions || 0),
            clicks: sum.clicks + (campaign.clicks || 0),
            leadCount: sum.leadCount + (campaign.leadCount || 0),
            sqlReal: sum.sqlReal + (campaign.sqlReal || 0),
            salesTbc: sum.salesTbc + (campaign.salesTbc || 0),
            totalReal: sum.totalReal + (campaign.totalReal || 0),
            salesWon: sum.salesWon + (campaign.salesWon || 0),
            salesWonAmount: sum.salesWonAmount + (campaign.salesWonAmount || 0),
            currency: campaign.currency || sum.currency || currency || 'INR',
        }), {
            spend: 0,
            impressions: 0,
            clicks: 0,
            leadCount: 0,
            sqlReal: 0,
            salesTbc: 0,
            totalReal: 0,
            salesWon: 0,
            salesWonAmount: 0,
            currency: currency || 'INR',
        });
    }

    const footerTotalsBase = rangeOf(campaigns);
    const footerTotals = withPeriodSalesWon(
        footerTotalsBase,
        dates,
        (date) => sumDays(campaigns, date),
    );
    const targetsSet = groups.filter(([name]) => cityTargets[name] != null);
    const targetTotal = targetsSet.reduce((sum, [name]) => sum + (cityTargets[name] || 0), 0);
    const achievedTotal = footerTotals.totalReal || 0;
    const achievedRate = targetsSet.length && targetTotal > 0
        ? Math.round((achievedTotal / targetTotal) * 100)
        : null;

    function jumpToTodayColumn() {
        const container = scrollRef.current;
        if (!container || !dates.includes(today)) return;
        const target = container.querySelector(`[data-day-page="${today}"]`);
        if (!target) return;
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const delta = targetRect.left - containerRect.left - STICKY_TOTAL_WIDTH;
        container.scrollTo({ left: Math.max(0, container.scrollLeft + delta), behavior: 'smooth' });
    }

    function handleDownloadExcel(detailLevel = 'city') {
        if (!groups.length || !dates.length) return;
        setExportMenuAnchor(null);
        downloadGoogleCampaignSpendTableXlsx({
            from,
            to,
            scopeLabel: scopeLabel || 'All cities',
            dates,
            groups,
            aggregateRange: rangeOf,
            sumDay: sumDays,
            footerTotals,
            footerDay: (date) => sumDays(campaigns, date),
            cityTargets,
            detailLevel,
        });
    }

    return (
        <section className={classes.card}>
            <div className={classes.heading}>
                <h2 className={classes.title}>{tableTitle}</h2>
                <span className={classes.count}>
                    {scopeLabel || 'All cities'}
                    {' · '}
                    {campaigns.length} campaigns
                    {' · '}
                    Target {targetsSet.length ? formatNumber(targetTotal) : '—'}
                    {' · '}
                    Total Real {formatNumber(achievedTotal)}
                    {achievedRate != null ? ` · ${achievedRate}%` : ''}
                </span>
                <Button
                    className={classes.exportBtn}
                    variant="contained"
                    size="small"
                    startIcon={<GetAppIcon />}
                    disabled={!groups.length || loading || !dates.length}
                    onClick={(event) => setExportMenuAnchor(event.currentTarget)}
                >
                    Download Excel
                </Button>
                <Menu
                    anchorEl={exportMenuAnchor}
                    open={Boolean(exportMenuAnchor)}
                    onClose={() => setExportMenuAnchor(null)}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MenuItem onClick={() => handleDownloadExcel('city')}>
                        City summary only
                    </MenuItem>
                    <MenuItem onClick={() => handleDownloadExcel('full')}>
                        Full breakdown (campaigns per city)
                    </MenuItem>
                </Menu>
            </div>
            {loading ? (
                <p className={classes.empty}>Loading campaign expenditure...</p>
            ) : campaigns.length === 0 ? (
                <p className={classes.empty}>
                    {hasError ? 'Campaign expenditure could not be loaded.' : (emptyMessage || 'No campaign data found for this date range.')}
                </p>
            ) : (
                <>
                    <p className={classes.scrollHint}>
                        {onSaveTarget
                            ? 'Set lead targets on each city row (bold name with campaign count), not on individual campaign rows. '
                            : 'City targets show Meta + Google targets combined (edit targets on the Meta Lead or Google Leads tabs). '}
                        City names stay fixed; range totals appear next, then day columns (newest date first — scroll right for older days).
                        {dates.includes(today) ? (
                            <>
                                {' '}
                                Today (
                                {formatDayLabel(today)}
                                ) is highlighted.
                                <button type="button" className={classes.jumpTodayBtn} onClick={jumpToTodayColumn}>
                                    Jump to today
                                </button>
                            </>
                        ) : null}
                    </p>
                    <div className={classes.tableScroll} ref={scrollRef}>
                        <table
                            className={classes.table}
                            style={{
                                minWidth: STICKY_TOTAL_WIDTH
                                    + PERIOD_COLUMN_COUNT * 96
                                    + dates.length * METRICS_PER_DAY * 76,
                            }}
                        >
                            <colgroup>
                                <col style={{ width: STICKY_LEAD_WIDTH }} />
                                <col style={{ width: STICKY_CAMPAIGN_WIDTH }} />
                                <col style={{ width: 130 }} />
                                <col style={{ width: 110 }} />
                                <col style={{ width: 84 }} />
                                <col style={{ width: 84 }} />
                                <col style={{ width: 80 }} />
                                <col style={{ width: 108 }} />
                                <col style={{ width: 84 }} />
                                <col style={{ width: 84 }} />
                                <col style={{ width: 96 }} />
                                <col style={{ width: 96 }} />
                                <col style={{ width: 84 }} />
                                <col style={{ width: 112 }} />
                                <col style={{ width: 112 }} />
                                {dates.map((date) => (
                                    <React.Fragment key={`col-${date}`}>
                                        {Array.from({ length: METRICS_PER_DAY }).map((_, index) => (
                                            <col key={`${date}-${index}`} style={{ width: 76 }} />
                                        ))}
                                    </React.Fragment>
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.stickyLead}`} rowSpan={2} aria-label="Expand" />
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.stickyNameHead}`} rowSpan={2}>Campaign</th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead}`} rowSpan={2}>Spend</th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead}`} rowSpan={2}>Impressions</th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead}`} rowSpan={2}>Clicks</th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead}`} rowSpan={2}>CPC</th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead}`} rowSpan={2}>Target</th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                        Leads
                                        <br />
                                        received
                                    </th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                        SQL-
                                        <br />
                                        Real
                                    </th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                        Sales-
                                        <br />
                                        TBC
                                    </th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                        Total
                                        <br />
                                        Real
                                    </th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                        Sales
                                        <br />
                                        Won
                                    </th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                        Sales Won
                                        <br />
                                        value
                                    </th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead} ${classes.headStack}`} rowSpan={2}>
                                        %
                                        <br />
                                        Achieved
                                    </th>
                                    <th className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.numHead}`} rowSpan={2}>CPERP</th>
                                    {dates.map((date, dateIndex) => (
                                        <th
                                            key={date}
                                            className={`${classes.headCell} ${classes.headCellStickyRow1} ${classes.datePageHead} ${dateIndex === 0 ? classes.datePageHeadSectionStart : ''} ${date === today ? classes.datePageHeadToday : ''}`}
                                            colSpan={METRICS_PER_DAY}
                                            data-day-page={date}
                                        >
                                            Date ·
                                            {' '}
                                            {formatDayLabel(date)}
                                            {date === today ? ' · Today' : ''}
                                        </th>
                                    ))}
                                </tr>
                                <tr>
                                    {dates.map((date, dateIndex) => (
                                        <React.Fragment key={`sub-${date}`}>
                                            <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead} ${dateIndex === 0 ? classes.datePageHeadSectionStart : ''}`}>Spend</th>
                                            <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead}`}>Leads</th>
                                            <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead}`}>SQL</th>
                                            <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead}`}>TBC</th>
                                            <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead}`}>Real</th>
                                            <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead}`}>Won</th>
                                            <th className={`${classes.headCell} ${classes.headCellStickyRow2} ${classes.numHead} ${classes.dateSubHead}`}>CPERP</th>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map(([cityName, rows]) => {
                                    const open = !!openCities[cityName];
                                    const cityMetrics = withPeriodSalesWon(
                                        rangeOf(rows),
                                        dates,
                                        (date) => sumDays(rows, date),
                                    );
                                    return (
                                        <React.Fragment key={cityName}>
                                            <tr
                                                className={classes.cityTotalRow}
                                                onClick={(event) => {
                                                    if (isTargetInteraction(event)) return;
                                                    setOpenCities((current) => ({ ...current, [cityName]: !current[cityName] }));
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                aria-expanded={open}
                                                onKeyDown={(event) => {
                                                    if (isTargetInteraction(event)) return;
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        setOpenCities((current) => ({ ...current, [cityName]: !current[cityName] }));
                                                    }
                                                }}
                                            >
                                                <td className={`${classes.bodyCell} ${classes.stickyLeadBody}`}>
                                                    <IconButton size="small" tabIndex={-1} aria-hidden onClick={(e) => { e.stopPropagation(); setOpenCities((c) => ({ ...c, [cityName]: !c[cityName] })); }}>
                                                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </IconButton>
                                                </td>
                                                <td className={`${classes.bodyCell} ${classes.stickyNameBody}`}>
                                                    <span className={classes.name}>{cityName}</span>
                                                    <small className={classes.id}>
                                                        {rows.length}
                                                        {' '}
                                                        campaign
                                                        {rows.length === 1 ? '' : 's'}
                                                    </small>
                                                </td>
                                                <GooglePeriodCells
                                                    classes={classes}
                                                    metrics={cityMetrics}
                                                    rows={rows}
                                                    label={cityName}
                                                    dates={dates}
                                                    today={today}
                                                    getDayMetrics={(date) => ({ ...sumDays(rows, date), date })}
                                                    onOpenLeads={onOpenLeads}
                                                    mixedCurrency={mixedCurrency}
                                                    currency={currency}
                                                    targetCity={cityName}
                                                    savedTarget={cityTargets[cityName]}
                                                    onSaveTarget={onSaveTarget}
                                                />
                                            </tr>
                                            {open ? rows.map((campaign, index) => (
                                                <tr key={`${cityName}-${campaign.campaignId}`} className={index % 2 ? classes.rowAlt : undefined}>
                                                    <td className={`${classes.bodyCell} ${classes.stickyLeadBody}`} />
                                                    <td className={`${classes.bodyCell} ${classes.stickyNameBody}`}>
                                                        {campaign.channel === 'google' ? (
                                                            <span className={classes.sub}>Google</span>
                                                        ) : null}
                                                        {campaign.channel === 'meta' ? (
                                                            <span className={classes.sub}>Meta</span>
                                                        ) : null}
                                                        <span className={classes.name}>{campaign.campaignName}</span>
                                                        {campaign.sourceRow ? <span className={classes.sub}>Source</span> : null}
                                                        {!hideCityTagging && campaign.channel !== 'meta' && !campaign.sourceRow
                                                            && (cityName === 'Untagged' || campaign.citySource === 'manual') && (
                                                            <TextField
                                                                className={classes.cityField}
                                                                select
                                                                size="small"
                                                                variant="outlined"
                                                                label="Tag city"
                                                                value={campaign.citySource === 'manual' ? (campaign.cities?.[0] || '') : ''}
                                                                disabled={taggingId === (campaign.reportingCampaignId || campaign.campaignId)}
                                                                onClick={(event) => event.stopPropagation()}
                                                                onChange={(event) => {
                                                                    const id = campaign.reportingCampaignId || campaign.campaignId;
                                                                    if (event.target.value && onTagCity) onTagCity(id, event.target.value);
                                                                }}
                                                            >
                                                                <MenuItem value="">Select city</MenuItem>
                                                                {(cityOptions || []).map((name) => (
                                                                    <MenuItem key={name} value={name}>{name}</MenuItem>
                                                                ))}
                                                            </TextField>
                                                        )}
                                                        <small className={classes.id}>{campaign.reportingCampaignId || campaign.campaignId}</small>
                                                    </td>
                                                    <GooglePeriodCells
                                                        classes={classes}
                                                        metrics={{
                                                            ...withPeriodSalesWon(
                                                                rangeOf([campaign]),
                                                                dates,
                                                                (date) => campaignDay(campaign, date),
                                                            ),
                                                            currency: campaign.currency || currency,
                                                        }}
                                                        rows={[campaign]}
                                                        label={campaign.campaignName}
                                                        dates={dates}
                                                        today={today}
                                                        getDayMetrics={(date) => ({ ...campaignDay(campaign, date), date })}
                                                        onOpenLeads={onOpenLeads}
                                                        mixedCurrency={mixedCurrency}
                                                        currency={currency}
                                                    />
                                                </tr>
                                            )) : null}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            {groups.length > 1 ? (
                                <tfoot>
                                    <tr className={classes.totalRow}>
                                        <td className={`${classes.bodyCell} ${classes.stickyLeadBody}`} />
                                        <td className={`${classes.bodyCell} ${classes.stickyNameBody}`}>All cities total</td>
                                        <GooglePeriodCells
                                            classes={classes}
                                            metrics={footerTotals}
                                            rows={campaigns}
                                            label={scopeLabel || 'All cities'}
                                            dates={dates}
                                            today={today}
                                            getDayMetrics={(date) => ({ ...sumDays(campaigns, date), date })}
                                            onOpenLeads={onOpenLeads}
                                            mixedCurrency={mixedCurrency}
                                            currency={currency}
                                            displayTarget={targetsSet.length ? targetTotal : null}
                                            displayAchievedPercent={achievedRate}
                                        />
                                    </tr>
                                </tfoot>
                            ) : null}
                        </table>
                    </div>
                </>
            )}
        </section>
    );
}
